import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

export function initLogoWallCycleTeam(scope = document) {
    const loopDelay = 3
    const duration = 0.9
    const batchSize = 4
    const batchStagger = 0.08

    scope.querySelectorAll('[data-logo-wall-cycle-team-init]').forEach((root) => {
        const list = root.querySelector('[data-logo-wall-team-list]')
        if (!list) return
        const isMobile = window.matchMedia('(max-width: 767px)')

        const items = Array.from(list.querySelectorAll('[data-logo-wall-team-item]'))
        const shuffleFront =
            root.getAttribute('data-logo-wall-team-shuffle') !== 'false'
        const originalTargets = items
            .map((item) => item.querySelector('[data-logo-wall-team-target]'))
            .filter(Boolean)
        const totalTargets = originalTargets.length

        let visibleItems = []
        let visibleCount = 0
        let visibleTargetIndexes = []
        let poolIndexes = []
        let pattern = []
        let patternIndex = 0
        let tl

        function isVisible(el) {
            return window.getComputedStyle(el).display !== 'none'
        }

        function shuffleArray(arr) {
            const a = arr.slice()
            for (let i = a.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1))
                ;[a[i], a[j]] = [a[j], a[i]]
            }
            return a
        }

        function setup() {
            if (tl) tl.kill()
            tl = null

            visibleItems = items.filter(isVisible)
            visibleCount = visibleItems.length
            if (!visibleCount || !totalTargets) return

            if (isMobile.matches) {
                return
            }

            pattern = shuffleArray(
                Array.from({ length: visibleCount }, (_, i) => i)
            )
            patternIndex = 0

            items.forEach((item) => {
                item.querySelectorAll('[data-logo-wall-team-target]').forEach((old) =>
                    old.remove()
                )
            })

            const allTargetIndexes = Array.from(
                { length: originalTargets.length },
                (_, i) => i
            )

            let front, rest
            if (shuffleFront) {
                const shuffledAll = shuffleArray(allTargetIndexes)
                front = shuffledAll.slice(0, visibleCount)
                rest = shuffleArray(shuffledAll.slice(visibleCount))
            } else {
                front = allTargetIndexes.slice(0, visibleCount)
                rest = shuffleArray(allTargetIndexes.slice(visibleCount))
            }

            visibleTargetIndexes = front
            poolIndexes = rest

            for (let i = 0; i < visibleTargetIndexes.length; i++) {
                const parent =
                    visibleItems[i].querySelector('[data-logo-wall-team-target-parent]') ||
                    visibleItems[i]
                const target = originalTargets[visibleTargetIndexes[i]].cloneNode(true)
                gsap.set(target, { autoAlpha: 1, yPercent: 0 })
                parent.appendChild(target)
            }

            if (totalTargets <= batchSize) return

            tl = gsap.timeline({ repeat: -1, repeatDelay: loopDelay })
            tl.call(swapBatch)
            tl.play()
        }

        function nextPatternIndices(count) {
            const picked = []
            for (let i = 0; i < count; i++) {
                if (patternIndex >= pattern.length) {
                    pattern = shuffleArray(
                        Array.from({ length: visibleCount }, (_, idx) => idx)
                    )
                    patternIndex = 0
                }
                picked.push(pattern[patternIndex++])
            }
            return picked
        }

        function swapBatch() {
            if (isMobile.matches) return

            const nowCount = items.filter(isVisible).length
            if (nowCount !== visibleCount) {
                setup()
                return
            }
            if (!visibleCount) return

            const activeBatchSize = Math.min(batchSize, visibleCount)
            const slotIndexes = nextPatternIndices(activeBatchSize).sort(
                (a, b) => a - b
            )

            const slots = slotIndexes
                .map((slotIndex) => {
                    const container = visibleItems[slotIndex]
                    const parent =
                        container.querySelector('[data-logo-wall-team-target-parent]') ||
                        container
                    const existing = parent.querySelectorAll(
                        '[data-logo-wall-team-target]'
                    )

                    if (existing.length > 1) return null

                    return {
                        slotIndex,
                        parent,
                        current: parent.querySelector('[data-logo-wall-team-target]'),
                        currentTargetIndex: visibleTargetIndexes[slotIndex]
                    }
                })
                .filter(Boolean)

            if (!slots.length) return

            const outgoingIndexes = slots.map((slot) => slot.currentTargetIndex)
            const incomingIndexes = []

            while (incomingIndexes.length < slots.length && poolIndexes.length) {
                const picked = poolIndexes.shift()
                incomingIndexes.push(picked)
            }

            if (incomingIndexes.length < slots.length) {
                if (slots.length === 1) return

                const rotatedOutgoing = outgoingIndexes
                    .slice(1)
                    .concat(outgoingIndexes[0])
                while (incomingIndexes.length < slots.length) {
                    incomingIndexes.push(rotatedOutgoing[incomingIndexes.length])
                }
            }

            const incomingFromOutgoing = incomingIndexes.filter((idx) =>
                outgoingIndexes.includes(idx)
            )
            const outgoingUsage = new Map()
            incomingFromOutgoing.forEach((idx) => {
                outgoingUsage.set(idx, (outgoingUsage.get(idx) || 0) + 1)
            })
            const outgoingBackToPool = outgoingIndexes.filter((idx) => {
                const used = outgoingUsage.get(idx) || 0
                if (!used) return true
                outgoingUsage.set(idx, used - 1)
                return false
            })

            poolIndexes = poolIndexes.concat(outgoingBackToPool)

            slots.forEach((slot, i) => {
                const incomingTargetIndex = incomingIndexes[i]
                if (incomingTargetIndex === undefined) return

                const staggerDelay = i * batchStagger
                visibleTargetIndexes[slot.slotIndex] = incomingTargetIndex

                const incoming = originalTargets[incomingTargetIndex].cloneNode(true)
                gsap.set(incoming, { yPercent: 16, autoAlpha: 0 })
                slot.parent.appendChild(incoming)

                if (slot.current) {
                    gsap.to(slot.current, {
                        yPercent: -16,
                        autoAlpha: 0,
                        duration,
                        delay: staggerDelay,
                        ease: 'expo.inOut',
                        onComplete: () => slot.current.remove()
                    })
                }

                gsap.to(incoming, {
                    yPercent: 0,
                    autoAlpha: 1,
                    duration,
                    delay: 0.1 + staggerDelay,
                    ease: 'expo.inOut'
                })
            })
        }

        setup()

        isMobile.addEventListener?.('change', () => setup())

        ScrollTrigger.create({
            trigger: root,
            start: 'top bottom',
            end: 'bottom top',
            onEnter: () => tl && tl.play(),
            onLeave: () => tl && tl.pause(),
            onEnterBack: () => tl && tl.play(),
            onLeaveBack: () => tl && tl.pause()
        })

        document.addEventListener(
            'visibilitychange',
            () => tl && (document.hidden ? tl.pause() : tl.play())
        )
    })
}
